import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { usersApi, type AdminUser } from '@/api/users';
import { coursesApi } from '@/api/courses';
import { useAuthStore } from '@/stores/auth';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { humanizeError } from '@/lib/errors';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { emailChanged, emailChangeError } from '@/lib/emailChange';
import type { Role } from '@/api/auth';

type StudentFormData = {
  full_name: string;
  email: string;
  phone?: string;
  password?: string;
  role: Role;
  marathon_id?: string;
};

type Issued = { email: string; password: string };

/** Учётка заведена, а запись на курс отказала: что показать и что повторить. */
type EnrollFailed = { user: AdminUser; marathonId: string; reason: string };

/** Итог сохранения. `enrollError` — учётка есть, записи нет (техдолг п. 169). */
type SaveResult = { user: AdminUser; issued?: Issued; enrollError?: string; marathonId?: string };

export type StudentFormModalProps = {
  open: boolean;
  onClose: () => void;
  initial?: AdminUser;
  defaultRole?: Role;
  onSaved?: (u: AdminUser) => void;
};

export function StudentFormModal({ open, onClose, initial, defaultRole, onSaved }: StudentFormModalProps) {
  // Закрытие окна обязано стирать показанный пароль: иначе он всплывёт при
  // следующем открытии формы — над именем уже другого человека.
  //
  // ⚠️ И ОШИБКУ ТОЖЕ (12.09.2026). Раньше `serverError` не сбрасывался нигде: отказ
  // прошлого заведения встречал администратора при следующем открытии окна.
  const close = () => {
    issuedRef.current = null;
    setIssued(null);
    setEnrollFailed(null);
    setServerError(null);
    setCopied(false);
    onClose();
  };
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isEdit = !!initial;
  // Роль последнего сохранения — её и оставляем в форме, чтобы следующего
  // человека не завести с чужой ролью.
  const lastRoleRef = useRef<Role>(defaultRole ?? 'student');
  const [serverError, setServerError] = useState<string | null>(null);

  // Only a director (role==='admin') may grant the privileged staff roles
  // (admin/manager). A manager must not be able to mint admins or other
  // managers. Server-side RBAC is the real guard; this only hides the options.
  const currentRole = useAuthStore((s) => s.user?.role);
  const canGrantStaff = currentRole === 'admin';
  // Почту в правке меняет только admin — так закрыта и ручка auth. Менеджеру поле видно, но не правится.
  const canChangeEmail = currentRole === 'admin';
  const roleOptions = [
    { value: 'student', label: t('studentForm.roleStudent') },
    { value: 'curator', label: t('studentForm.roleCurator') },
    ...(canGrantStaff
      ? [
          { value: 'manager', label: t('studentForm.roleManager') },
          { value: 'admin', label: t('studentForm.roleAdmin') },
        ]
      : []),
  ];

  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm<StudentFormData>({
    defaultValues: initial
      ? {
          full_name: initial.full_name,
          email: initial.email,
          phone: initial.phone,
          role: initial.role,
        }
      : { role: defaultRole ?? 'student' },
  });
  const role = watch('role');

  // ⚠️ ВЫБИРАЕМ КУРС, А НЕ ТАРИФ (переезд 07.09.2026).
  //
  // Раньше здесь выбирали тариф, и курс брался из него — школа же мыслит
  // курсами. Права теперь живут на самом курсе, поэтому и записывать надо
  // прямо на него: лишнее звено убрано.
  const courses = useQuery({
    queryKey: ['admin', 'marathons', 'for-enroll'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100 }),
  });
  const enrollableCourses = (courses.data?.items ?? []).filter(
    (m: { status?: string; is_template?: boolean }) => !m.is_template,
  );

  const tariffs = useQuery({
    queryKey: ['admin', 'tariffs'],
    queryFn: () => coursesApi.listTariffs(),
    enabled: open && !isEdit && !IS_APP_BUILD,
  });
  const enrollable = (tariffs.data ?? []).filter((tp) => tp.is_active && tp.marathon_id);

  // Пароль, который платформа выдала только что заведённому ученику. Показываем
  // его прямо в окне: администратор обычно заводит человека при живом разговоре
  // и диктует доступ сразу, не дожидаясь письма.
  const [issued, setIssued] = useState<Issued | null>(null);
  // Выданный пароль держим и тогда, когда запись на курс отказала: после
  // успешного повтора он показывается как обычно.
  const issuedRef = useRef<Issued | null>(null);
  const [copied, setCopied] = useState(false);
  const [enrollFailed, setEnrollFailed] = useState<EnrollFailed | null>(null);

  const save = useMutation({
    mutationFn: async (data: StudentFormData): Promise<SaveResult> => {
      if (isEdit && initial) {
        // ⚠️ ПОЧТА — ОТДЕЛЬНЫМ ВЫЗОВОМ В auth, И ПЕРВОЙ (28.09.2026, договор admin-email-change.md). Раньше она
        // уходила в users вместе с остальным и там молча выбрасывалась. Первой — потому что её отказ самый
        // вероятный («адрес занят»): при нём не сохраняется ничего, а не половина правки.
        // Остальное уходит в users без почты: поле там не пишется.
        const { email, ...rest } = data;
        if (canChangeEmail && emailChanged(initial.email, email)) await usersApi.changeEmail(initial.id, email);
        return { user: await usersApi.update(initial.id, rest) };
      }

      // Ученик и сотрудник заводятся по-разному, и это не прихоть.
      //
      // Ученику платформа выдаёт пароль сама (шесть цифр), отправляет письмо с
      // логином, паролем и ссылкой, и хранит пароль так, что его видно в
      // карточке. Сотруднику пароль задаёт администратор: у него доступ к чужим
      // данным, и такой пароль обязан быть сложным.
      lastRoleRef.current = data.role;
      if (data.role !== 'student') {
        return {
          user: await usersApi.create({
            full_name: data.full_name,
            email: data.email,
            phone: data.phone,
            password: data.password,
            role: data.role,
          }),
        };
      }
      const res = await usersApi.createStudent({
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
      });
      const issuedNow = { email: res.user.email, password: res.password };
      // Курс выбран — записываем сразу, чтобы доступ был без второго шага.
      // Тариф не передаём вовсе: права ученик получит от самого курса, а
      // дополнительные курсы выдадутся по его настройкам.
      //
      // ⚠️ ОТКАЗ ЗАПИСИ — НЕ ОШИБКА ЗАВЕДЕНИЯ (техдолг п. 169, 12.09.2026). Учётка к
      // этому моменту уже заведена и письмо ушло. Раньше экран «пароль выдан»
      // включался ДО записи, а отказ уходил в ошибку, которую рисует только
      // форма: администратор видел «выдано», не видел ни отказа, ни ученика в
      // списке — и заводил человека заново. На проде у 8 из 12 удалённых учёток
      // двойник заведён через 1–65 минут.
      if (data.marathon_id) {
        try {
          // Срок не шлём: решает сервер по курсу — 90 дней, «один на один» — 365, доп. курсы как основной
          // (11.10.2026; до этого здесь было 365 любому курсу).
          await usersApi.enroll({
            student_id: res.user.id,
            marathon_id: data.marathon_id,
          });
        } catch (e) {
          return { user: res.user, issued: issuedNow, enrollError: humanizeError(e), marathonId: data.marathon_id };
        }
      }
      return { user: res.user, issued: issuedNow };
    },
    onMutate: () => setServerError(null),
    onSuccess: (r) => {
      // Ученик появляется в списке и тогда, когда запись отказала: учётка есть, и
      // не показать её — ровно то, из-за чего заводили двойников.
      onSaved?.(r.user);
      // ⚠️ РОЛЬ СОХРАНЯЕМ, ОСТАЛЬНОЕ ОЧИЩАЕМ. Пустой `reset()` возвращал роль к
      // значению по умолчанию, а на странице «Кураторы» оно — «куратор».
      // Выглядело так: администратор ставит «Администратор», сохраняет — человек
      // создан верно, форма молча возвращает «Куратор», и следующие заводятся
      // кураторами. Заметить нельзя: поле выглядит заполненным, просто не тем.
      // Ровно так 06.09.2026 двое из четверых будущих админов стали кураторами.
      //
      // Людей одной роли заводят пачкой, поэтому сохранить выбранную роль —
      // это и защита от ошибки, и меньше работы.
      reset({ role: lastRoleRef.current });
      if (r.issued) issuedRef.current = r.issued;
      // «Пароль выдан» — только когда ученик и заведён, и записан на курс.
      if (r.enrollError && r.marathonId) {
        setEnrollFailed({ user: r.user, marathonId: r.marathonId, reason: r.enrollError });
        return;
      }
      // Окно остаётся открытым, пока показан выданный пароль: закрыть его сразу
      // значит потерять единственный момент, когда пароль виден администратору
      // на экране. Из карточки его потом достать можно, но лишним шагом.
      if (r.issued) {
        setIssued(r.issued);
        return;
      }
      onClose();
    },
    onError: (e: unknown) => setServerError(isEdit ? emailChangeError(e) : humanizeError(e)),
  });

  // Повтор записи того же ученика — без повторного заведения учётки.
  const retryEnroll = useMutation({
    mutationFn: (f: EnrollFailed) =>
      usersApi.enroll({ student_id: f.user.id, marathon_id: f.marathonId }),
    onSuccess: () => {
      setEnrollFailed(null);
      setIssued(issuedRef.current);
    },
    onError: (e: unknown, f) => setEnrollFailed({ ...f, reason: humanizeError(e) }),
  });

  // На телефоне «Отмена / Сохранить» — внизу окна во весь экран (правило 3); полоса вне <form>, отправка — по `form`.
  const formButtons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button type="button" variant="ghost" onClick={close}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" form="student-form" loading={save.isPending}>
        {isEdit ? t('common.save') : t('studentForm.create')}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEdit ? t('studentForm.editTitle') : t('studentForm.createTitle')}
      phoneFull
      footer={isMobile && !issued && !enrollFailed ? formButtons : undefined}
    >
      {issued ? (
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">{t('studentForm.issuedIntro')}</p>

          <div className="rounded-xl border border-outline-variant bg-surface-variant/40 p-4 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                {t('studentForm.issuedLogin')}
              </p>
              <p className="mt-1 break-all font-medium">{issued.email}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                {t('studentForm.issuedPassword')}
              </p>
              <p className="mt-1 text-2xl font-bold tracking-[0.3em] tabular-nums">{issued.password}</p>
            </div>
          </div>

          <p className="text-xs text-on-surface-variant">{t('studentForm.issuedMailed')}</p>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                // Копируем логин и пароль вместе: администратор вставляет их в
                // переписку одним куском, а не собирает по частям.
                void navigator.clipboard
                  ?.writeText(`${issued.email}\n${issued.password}`)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              {copied ? t('studentForm.issuedCopied') : t('studentForm.issuedCopy')}
            </Button>
            <Button type="button" onClick={close}>
              {t('common.done')}
            </Button>
          </div>
        </div>
      ) : enrollFailed ? (
        <div className="space-y-4" data-enroll-failed="">
          <div role="alert" className="rounded-xl bg-error-container px-4 py-3 text-sm text-on-error-container">
            <p className="font-semibold">{t('studentForm.enrollFailedTitle')}</p>
            <p className="mt-1">{t('studentForm.enrollFailedText')}</p>
            {enrollFailed.reason && <p className="mt-1">{enrollFailed.reason}</p>}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <Link
              to={`/admin/students/${enrollFailed.user.id}`}
              onClick={close}
              className="inline-flex items-center rounded-button px-4 py-2 text-sm font-semibold text-primary hover:bg-surface-container"
            >
              {t('studentForm.openCard')}
            </Link>
            <Button type="button" variant="ghost" onClick={close}>
              {t('common.done')}
            </Button>
            <Button type="button" loading={retryEnroll.isPending} onClick={() => retryEnroll.mutate(enrollFailed)}>
              {t('studentForm.enrollRetry')}
            </Button>
          </div>
        </div>
      ) : (
      <form id="student-form" onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
        <Input
          label={t('studentForm.labelFullName')}
          hint={t('studentForm.hintFullName')}
          {...register('full_name', { required: true, minLength: 2 })}
          error={errors.full_name && t('studentForm.errorFullName')}
        />
        <Input
          type="email"
          label={t('studentForm.labelEmail')}
          hint={isEdit && !canChangeEmail ? t('studentForm.emailAdminOnly') : t('studentForm.hintEmail')}
          readOnly={isEdit && !canChangeEmail}
          {...register('email', { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })}
          error={errors.email && t('studentForm.errorEmail')}
        />
        <Input
          label={t('studentForm.labelPhone')}
          placeholder="+7 700 …"
          {...register('phone')}
        />
        <Select
          label={t('studentForm.labelRole')}
          options={roleOptions}
          {...register('role')}
        />
        {!isEdit && role === 'student' && (
          <div>
            <Select
              label={t('studentForm.labelCourse')}
              options={[
                { value: '', label: t('studentForm.courseNone') },
                ...enrollableCourses.map((m: { id: string; title: string }) => ({
                  value: m.id,
                  label: m.title,
                })),
              ]}
              {...register('marathon_id')}
            />
            {/*
              ⚠️ ПУСТОЙ СПИСОК ОБЪЯСНЯЕТ СЕБЯ.
              04.09.2026 заказчик завёл тариф, не нашёл его здесь и потратил
              время на поиск причины: тариф был без курса, а восемь других —
              неактивны. Список молча показывал только «Без тарифа».
            */}
            {/* Подсказки про тариф — только на сайте (решение владельца 13.10.2026: в приложении ни слова «тариф»). */}
            {!IS_APP_BUILD && tariffs.data && tariffs.data.length > 0 && enrollable.length === 0 && (
              <p className="mt-1 text-xs text-warning">{t('studentForm.tariffNoneUsable')}</p>
            )}
            {!IS_APP_BUILD && <p className="mt-1 text-xs text-on-surface-variant">{t('studentForm.tariffHint')}</p>}
          </div>
        )}
        {/*
          Пароль вводится только для сотрудников. Ученику платформа выдаёт его
          сама и присылает письмом — заставлять администратора придумывать
          пароль за ученика незачем, а держать два разных способа заведения
          значит однажды получить ученика без письма.
        */}
        {!isEdit && role !== 'student' && (
          <Input
            type="password"
            label={t('studentForm.labelTempPassword')}
            hint={t('studentForm.hintTempPassword')}
            {...register('password', { minLength: 8 })}
            error={errors.password && t('studentForm.errorPassword')}
          />
        )}
        {!isEdit && role === 'student' && (
          <p className="text-xs text-on-surface-variant">{t('studentForm.passwordIssuedHint')}</p>
        )}

        {serverError && <p className="text-sm text-error">{serverError}</p>}

        {!isMobile && formButtons}
      </form>
      )}
    </Modal>
  );
}
